'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Menu, X, Instagram, Music } from 'lucide-react';
import Image from 'next/image';

interface HeaderProps {
  onTruckClick: () => void;
  onSocialClick: (platform: string) => void;
}

export default function Header({ onTruckClick, onSocialClick }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const router = useRouter();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleNavClick = (section: string) => {
    closeMobileMenu();
    // Scroll to section or handle navigation
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-gradient-to-br from-teal-400 to-emerald-400 shadow-lg">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          {/* Logo and Brand */}
          <div className="flex items-center gap-3 sm:gap-4">
            <Image
              src="/assets/Captain Monster Truck 1-Photoroom.png"
              alt="Captain Monster Truck logo"
              width={60}
              height={60}
              className="w-12 h-12 sm:w-15 sm:h-15 object-contain animate-spin-slow filter drop-shadow-lg cursor-pointer hover:scale-105 transition-transform"
              onClick={onTruckClick}
            />
            <div className="text-white font-bungee text-xl sm:text-2xl font-bold drop-shadow-lg">
              CAPTAIN MONSTER TRUCK
            </div>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex gap-3 lg:gap-4">
            <Button 
              variant="outline" 
              className="bg-orange-400 hover:bg-orange-500 text-white border-white hover:border-white text-sm lg:text-base rounded-xl"
              onClick={() => handleNavClick('about')}
            >
              About
            </Button>
            <Button 
              variant="outline" 
              className="bg-orange-400 hover:bg-orange-500 text-white border-white hover:border-white text-sm lg:text-base rounded-xl"
              onClick={() => handleNavClick('features')}
            >
              Features
            </Button>
            <Button 
              variant="outline" 
              className="bg-red-500 hover:bg-red-600 text-white border-white hover:border-white text-sm lg:text-base rounded-xl"
              onClick={() => handleNavClick('pricing')}
            >
              Pricing
            </Button>
            <Button
              variant="outline"
              className="bg-pink-400 hover:bg-pink-500 text-white border-white hover:border-white text-sm lg:text-base rounded-xl"
              onClick={() => router.push('/contact')}
            >
              Contact
            </Button>
          </div>
          
          {/* Desktop Social Links + Login */}
          <div className="hidden md:flex items-center gap-3">
            <span className="text-white font-semibold text-sm lg:text-base">FOLLOW US</span>
            <div className="flex gap-2">
              <button
                onClick={() => onSocialClick('instagram')}
                className="w-8 h-8 lg:w-10 lg:h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 hover:scale-110 transition-all cursor-pointer"
                aria-label="Follow us on Instagram"
              >
                <Instagram className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
              </button>
              <button
                onClick={() => onSocialClick('tiktok')}
                className="w-8 h-8 lg:w-10 lg:h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 hover:scale-110 transition-all cursor-pointer"
                aria-label="Follow us on TikTok"
              >
                <Music className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
              </button>
            </div>
            <div className="ml-4">
              <Button 
                variant="secondary"
                className="bg-yellow-300 hover:bg-yellow-400 text-emerald-900 font-semibold text-sm lg:text-base rounded-xl"
                onClick={() => router.push('/login')}
              >
                Login
              </Button>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="sm"
              className="text-white hover:bg-white/20 p-2"
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-white/20">
            <div className="flex flex-col gap-3 pt-4">
              <Button 
                variant="outline" 
                className="bg-orange-400 hover:bg-orange-500 text-white border-white hover:border-white justify-start rounded-xl"
                onClick={() => handleNavClick('about')}
              >
                About
              </Button>
              <Button 
                variant="outline" 
                className="bg-orange-400 hover:bg-orange-500 text-white border-white hover:border-white justify-start rounded-xl"
                onClick={() => handleNavClick('features')}
              >
                Features
              </Button>
              <Button 
                variant="outline" 
                className="bg-red-500 hover:bg-red-600 text-white border-white hover:border-white justify-start rounded-xl"
                onClick={() => handleNavClick('pricing')}
              >
                Pricing
              </Button>
              <Button
                variant="outline"
                className="bg-pink-400 hover:bg-pink-500 text-white border-white hover:border-white justify-start rounded-xl"
                onClick={() => router.push('/contact')}
              >
                Contact
              </Button>
              <Button 
                variant="outline" 
                className="bg-yellow-300 hover:bg-yellow-400 text-emerald-900 border-white hover:border-white justify-start rounded-xl"
                onClick={() => router.push('/login')}
              >
                Login
              </Button>
              
              {/* Mobile Social Links */}
              <div className="flex items-center gap-3 pt-2">
                <span className="text-white font-semibold text-sm">FOLLOW US</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => onSocialClick('instagram')}
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 hover:scale-110 transition-all cursor-pointer"
                    aria-label="Follow us on Instagram"
                  >
                    <Instagram className="w-4 h-4 text-white" />
                  </button>
                  <button
                    onClick={() => onSocialClick('tiktok')}
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 hover:scale-110 transition-all cursor-pointer"
                    aria-label="Follow us on TikTok"
                  >
                    <Music className="w-4 h-4 text-white" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
